import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AlertIcon, AlertService, CustomAlertComponent, FormGroupErrorMessageComponent, LoadingPanelService, noWhitespaceValidator, Utils } from 'lib-base';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { SecUsuarioService } from '../../../services/database/secUsuario.service';
import { SolicitudRestablecerPasswordRequest } from '../../../shared/interfaces/database/seguridad';
import { Router } from '@angular/router';

@Component({
  selector: 'app-password-reset-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormGroupErrorMessageComponent, ButtonModule, InputTextModule, RecaptchaV3Module, CustomAlertComponent],
  templateUrl: './password-reset-form.component.html',
  styleUrl: './password-reset-form.component.scss'
})
export class PasswordResetFormComponent implements OnInit, OnDestroy {

  form !: FormGroup;
  translations: any = {};
  currentTime !: Date;
  location!: string;
  recaptchaSubscription !: Subscription;
  private _hideDialog !: Subscription;

  constructor(private _fb: FormBuilder, private _ms: MessageService, private _t: TranslateService, private _pu: SecUsuarioService,
    private recaptchaV3Service: ReCaptchaV3Service, private _as: AlertService, private _router: Router, private _lps: LoadingPanelService
  ) {
    this.configureForm();
  }

  ngOnDestroy(): void {
    if (this.recaptchaSubscription) {
      this.recaptchaSubscription.unsubscribe();
    }
    if (this._hideDialog) {
      this._hideDialog.unsubscribe();
    }
  }

  ngOnInit() {
    this.translate();
    this.updateLocation();
    this.updateTime();
  }

  configureForm() {
    this.form = this._fb.group({
      correo: [null, [Validators.required, noWhitespaceValidator]],
    });
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      warnSummary: this._t.get('form.msg.warn-summary'),
      successSummary: this._t.get('form.msg.success-summary'),
      errorSummary: this._t.get('form.msg.error-summary'),
      completeCampos: this._t.get('form.msg.complete-campos'),
      siguiente: this._t.get('form.button.siguiente'),
      emailSent: this._t.get('msg.email-sent'),
      correo: this._t.get('common.correo'),
      restablecerButton: this._t.get('msg.restablecer-button'),
      message: this._t.get('components.app-password-reset-form.subtitle'),
      welcome: this._t.get('common.bienvenido'),
      atras: this._t.get('form.button.atras')
    }));
  }

  onBack() {
    this._router.navigate(['public/login']);
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }
    this.recaptchaSubscription = this.recaptchaV3Service.execute('solicitudRestablecerPassword')
      .subscribe((token) => {

        let request = {
          correo: this.form.get('correo')?.value,
          captchaToken: token
        } as SolicitudRestablecerPasswordRequest

        this.solicitudRestablecerPassword(request);
      });
  }
  updateTime(): void {
    this.currentTime = new Date();
  }

  updateLocation(): void {
    const now = new Date();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = -now.getTimezoneOffset() / 60;
    const offsetString = offset >= 0 ? `+${offset}:00` : `${offset}:00`;
    this.location = `${timeZone} ${offsetString}`;
  }

  //#region API Calls
  private solicitudRestablecerPassword(request: SolicitudRestablecerPasswordRequest) {
    this._lps.show();
    this._pu.solicitudRestablecerPassword(request).subscribe({
      next: (response) => {
        this._lps.hide();
        this._hideDialog = this._as.show({
          icon: AlertIcon.INFO,
          title: this.translations.restablecerButton,
          text: this.translations.emailSent,
          showAccept: true,
          textAccept: this.translations.siguiente,
        }).subscribe(key => {
          this._router.navigate(['public/login']);
        });
      },
    });
  }
  //#endregion

}
